// <BirthdaysCard> — Home View Step 5 (T-044, plan §3 / spec §8).
//
// Horizontal-scroll carousel of upcoming birthdays (today + next 7
// days). Same shape as <AnniversariesCard> but the date label is
// relative ("Today" / "Tomorrow, Feb 26" / "Friday, Mar 3") and the
// year is intentionally absent — the privacy invariant lives in the
// API (T-041 strips year before serializing) and is reinforced here:
// this component never reads or formats a year, even from the upcoming
// weekday computation (which lives entirely in the *current* year).

import type { HomeBirthday } from '@pa/shared';
import { Avatar } from './Avatar';

export interface BirthdaysCardProps {
  birthdays: HomeBirthday[];
}

export function BirthdaysCard({ birthdays }: BirthdaysCardProps) {
  return (
    <section
      data-testid="home-birthdays"
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
    >
      <header className="p-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="text-sm font-bold text-slate-800">Birthdays</h3>
      </header>
      {birthdays.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400 italic">
          No birthdays this week
        </div>
      ) : (
        <ul className="p-6 flex gap-6 overflow-x-auto no-scrollbar">
          {birthdays.map((b) => (
            <li
              key={b.id}
              className="flex flex-col items-center text-center min-w-[90px] flex-shrink-0"
            >
              <Avatar
                name={b.name}
                avatarUrl={b.avatarUrl}
                sizeClass="w-14 h-14"
                className="border-2 border-slate-100 mb-2"
              />
              <p className="text-xs font-bold text-slate-800 leading-tight">
                {b.name}
              </p>
              <p
                className="text-[10px] text-slate-400 font-bold mt-0.5"
                data-testid="home-birthdays-date"
              >
                {formatBirthdayLabel(b)}
              </p>
              <p className="text-[10px] text-[#6344C6] font-semibold">🎂</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Per spec §8.2:
//   daysUntil === 0 → "Today"
//   daysUntil === 1 → "Tomorrow, MMM D"
//   daysUntil >= 2  → "Weekday, MMM D"
// `monthDay` is the canonical server-stripped "MM-DD" — we only ever
// use that for the month abbreviation + day number; the weekday comes
// from `today + daysUntil` using the *current* year (never the DOB
// year). Exposed for unit testing.
export function formatBirthdayLabel(
  b: Pick<HomeBirthday, 'monthDay' | 'daysUntil'>,
  now: Date = new Date(),
): string {
  if (b.daysUntil === 0) return 'Today';
  const monthAndDay = formatMonthDay(b.monthDay);
  if (b.daysUntil === 1) return `Tomorrow, ${monthAndDay}`;
  // Compute the upcoming day's weekday in the *current* UTC year. We
  // intentionally avoid building a Date from the DOB at all so the
  // year never enters the picture (defensive: the server's invariant
  // is the primary guard, this is belt-and-braces).
  const upcoming = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + b.daysUntil,
    ),
  );
  const weekday = WEEKDAYS[upcoming.getUTCDay()];
  return `${weekday}, ${monthAndDay}`;
}

function formatMonthDay(monthDay: string): string {
  // monthDay is server-stripped "MM-DD". No year in, no year out.
  const m = monthDay.match(/^(\d{2})-(\d{2})$/);
  if (!m) return monthDay;
  const monthIdx = Number(m[1]) - 1;
  const day = Number(m[2]);
  if (monthIdx < 0 || monthIdx > 11) return monthDay;
  return `${MONTHS_SHORT[monthIdx]} ${day}`;
}
